import { ChevronLeft } from "lucide-react";
import * as React from "react";
import { Button } from "../atoms/button.js";
import { StickyBar, type StickyBarProps } from "../molecules/sticky-bar.js";

/**
 * BackNavigationBar — top-of-page "Back to ___" sticky bar.
 *
 * Generic primitive used for cross-app navigation (e.g. wallet → cockpit
 * dashboard) or in-app back links. The bar is a thin opinionated wrapper
 * around `StickyBar`: it always renders the leading slot as a link button
 * with a left chevron and the supplied `label`.
 *
 * Defaults to `side="top"`, `tone="primary"`, `position="static"` — the
 * shape consumers want today. All of these (plus any `StickyBar` prop) can
 * be overridden by passing them through.
 *
 * Consumers own the destination: pass `href` for a plain anchor or
 * `onClick` for an SPA-router navigation. Pass both and the click handler
 * fires alongside the navigation; pass neither and the bar renders a
 * disabled-looking button (no-op).
 *
 * @example
 *   // Cross-app jump from wallet back to cockpit dashboard
 *   <BackNavigationBar
 *     href={`${cockpitUrl}/`}
 *     label="Back to Owner Dashboard"
 *     data-track="button-back-to-owner-dashboard"
 *   />
 *
 * @example
 *   // Same-app jump using a router callback
 *   <BackNavigationBar
 *     label="Back to billing"
 *     onClick={() => navigate("/billing")}
 *   />
 */
export interface BackNavigationBarProps extends Omit<StickyBarProps, "leading" | "children"> {
  /** Visible button label, e.g. "Back to Owner Dashboard". */
  label: React.ReactNode;
  /** Target URL — when set, the leading slot renders an anchor. */
  href?: string;
  /** Click handler — when set without `href`, renders a button instead. */
  onClick?: React.MouseEventHandler<HTMLElement>;
  /** Anchor target attr — forwarded only when `href` is set. */
  target?: React.HTMLAttributeAnchorTarget;
  /** Anchor rel attr — forwarded only when `href` is set. */
  rel?: string;
  /** Tracking attribute applied to the back button. */
  "data-track"?: string;
}

export const BackNavigationBar = React.forwardRef<HTMLDivElement, BackNavigationBarProps>(
  function BackNavigationBar(
    {
      "aria-label": ariaLabel,
      "data-track": dataTrack,
      className,
      href,
      label,
      onClick,
      position = "static",
      rel,
      side = "top",
      target,
      tone = "primary",
      ...props
    },
    ref,
  ) {
    const leading = href ? (
      <Button asChild variant="link" size="sm" data-track={dataTrack}>
        <a href={href} target={target} rel={rel} onClick={onClick}>
          <ChevronLeft aria-hidden />
          {label}
        </a>
      </Button>
    ) : (
      <Button type="button" variant="link" size="sm" onClick={onClick} data-track={dataTrack}>
        <ChevronLeft aria-hidden />
        {label}
      </Button>
    );

    return (
      <StickyBar
        ref={ref}
        aria-label={ariaLabel ?? (typeof label === "string" ? label : "Back navigation")}
        className={className}
        leading={leading}
        position={position}
        side={side}
        tone={tone}
        {...props}
      />
    );
  },
);
BackNavigationBar.displayName = "BackNavigationBar";
